import type { ApiErrorBody } from '@zuri/shared';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
  }
}

/** Empresa que esta aba espera estar ativa; o servidor recusa se a sessão mudou em outra aba. */
let expectedTenantId: string | null = null;
export function setExpectedTenant(id: string | null) {
  expectedTenantId = id;
}

type Listener = (e: ApiError) => void;
const listeners = new Set<Listener>();
export function onApiError(fn: Listener) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-zuri-client': 'web' };
  if (expectedTenantId) headers['x-tenant-id'] = expectedTenantId;
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK', 'Sem conexão com o servidor. Verifique sua internet e tente novamente.');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const e = (data as ApiErrorBody)?.error;
    const error = new ApiError(res.status, e?.code ?? 'ERROR', e?.message ?? 'Erro inesperado.', e?.fields);
    listeners.forEach((l) => l(error));
    throw error;
  }
  return data as T;
}

export const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/** Monta query string ignorando vazios. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Download autenticado (exportações e anexos) respeitando permissões do servidor. */
export async function download(path: string, fallbackName: string) {
  const headers: Record<string, string> = { 'x-zuri-client': 'web' };
  if (expectedTenantId) headers['x-tenant-id'] = expectedTenantId;
  const res = await fetch(`/api${path}`, { headers, credentials: 'same-origin' });
  if (!res.ok) {
    let msg = 'Não foi possível baixar o arquivo.';
    try {
      msg = ((await res.json()) as ApiErrorBody).error.message;
    } catch {
      /* corpo não-JSON */
    }
    throw new ApiError(res.status, 'DOWNLOAD_FAILED', msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/.exec(cd);
  const name = m ? decodeURIComponent(m[1] ?? m[2]) : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
